/* @HEADER
 * @version 1.1.1559 | 2026-11-04
 * @purpose The provider stack of the authenticated app and the global mounts that live between its layers — auth, entitlement, persistence (with the p2p hosts and the registry signal), messages (with the «no data found» screen and the host's dialogs), recording, navigation, the recording bridge (with the two global banners), tools — as ONE component, so App.tsx composes and does not nest (ZVX-DEV-6143, App.tsx-by-meaning S4).
 * @sidecar AuthenticatedAppProviders.tsx.header.md
 * @layer app | @hex adapter | @ctx app
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8982, ZVX-DEV-6143, ZVX-DEV-2561, ZVX-DEV-1349, ZVX-DEV-3432, ZVX-DEV-6064, ZVX-DEV-7381

import type { ReactNode } from 'react'
import { AuthProvider } from '../auth'
import type { AuthPort } from '../auth/AuthContext'
import { EntitlementProvider } from '../features/entitlement/public-api'
import { PersistenceSetup, RecordingContextBridge } from './PersistenceSetup'
import { OrgP2pTransportCoordinatorMount } from '../features/content-p2p/OrgP2pTransportCoordinatorMount'
import { CanvasP2pAutoSyncMount } from '../features/canvas/CanvasP2pAutoSyncMount'
import { BoardP2pAutoSyncMount } from '../features/kanban/BoardP2pAutoSyncMount'
import { BackgroundProjectPublishersMount } from '../features/content-p2p/BackgroundProjectPublishersMount'
import { SpacePrivacyRuleMount } from '../features/content-access/SpacePrivacyRuleMount'
import { RelayPullProgressLine } from '../features/content-p2p/RelayPullProgressLine'
import { RelayPullProgressProvider } from '../features/content-p2p/RelayPullProgressContext'
import { LinkRespondersProvider } from '../features/content-p2p/LinkRespondersProvider'
import { FeedbackContextPublisher } from '../features/feedback/FeedbackContextPublisher'
import { WikiP2pAutoSyncMount } from '../features/wiki/WikiP2pAutoSyncMount'
import { RegistryChangeSignalMount } from '../features/content-p2p/RegistryChangeSignalMount'
import { ProjectSettingsP2pSyncMount } from '../features/content-p2p/ProjectSettingsP2pSyncMount'
import { MessagesProvider } from '../shared/i18n/MessagesContext'
import { DataNotFoundMount } from '../features/settings/DataNotFoundMount'
import { RecordingProvider } from '../shared/audio/RecordingContext'
import { NavigationProvider } from './NavigationContext'
import { SoftReloginBanner } from '../features/auth/public-api'
import { ContentDeleteRefusedBanner } from '../features/content-p2p/ContentDeleteRefusedBanner'
import { CanvasWriteRefusedBanner } from '../features/canvas/CanvasWriteRefusedBanner'
import { ZvenixToolsProvider } from './ZvenixToolsProvider'

export interface AuthenticatedAppProvidersProps {
  /** The AuthPort for this mount (auth/chooseAuthAdapter.ts decides which). */
  adapter: AuthPort
  navigation: { canGoBack: boolean; onGoBack: () => void }
  /** What the recording bridge stamps on every recording: `route[:view]`. */
  recordingRoute: string
  /**
   * The host's dialogs — mounted right under MessagesProvider, beside the
   * «no data found» screen, so they have copy but no route or recording
   * context (they portal to <body>). The space-delete question lives here.
   */
  dialogs?: ReactNode
  children: ReactNode
}

/**
 * The order is the contract, and every layer has a reason to sit where it
 * sits:
 *
 *   AuthProvider            — everything below may read the session.
 *   EntitlementProvider     — the plan, read by the shell and the boards.
 *   PersistenceSetup        — the WS transport and the persistence adapter for
 *                             the whole app lifetime (ADR-0059 / ZVX-DEV-1312);
 *                             the p2p hosts and the registry signal mount right
 *                             under it because they need the transport and no
 *                             copy.
 *   FeedbackContextPublisher — keeps the complaint's flags and role current for
 *                             the feedback widget, which sits above all of this
 *                             (ZVX-DEV-7381).
 *   MessagesProvider        — copy; the «no data found» screen (ZVX-DEV-6064)
 *                             and the host's dialogs sit here.
 *   RecordingProvider       — audio recording state.
 *   NavigationProvider      — canGoBack / onGoBack for every screen.
 *   RecordingContextBridge  — stamps the route on recordings; the soft-relogin
 *                             strip (ZVX-DEV-1349) and the refused-deletion
 *                             banner (ZVX-DEV-3432) are global and asynchronous,
 *                             so they mount here, above any route.
 *   ZvenixToolsProvider     — KanbanPortsContext for @zvenix/tasks consumers;
 *                             ONCE, here (ZVX-DEV-2561): the shell's command
 *                             palette and every screen below share it.
 */
/**
 * ZVX-DEV-7838 -- the application's one pull-progress store (the counter behind the «cards are arriving» line) is
 * provided HERE, above everything that writes to it or reads it, and by composition: the providers below are not
 * re-indented for it.
 *
 * ZVX-DEV-8982 -- the same for the registry of the functions that answer a pull on the persistent link: the board's mount registers into it, the mount
 * that holds the transport looks into it, and both sit below this component, which makes it once and hands it down by context.
 */
export function AuthenticatedAppProviders(props: AuthenticatedAppProvidersProps) {
  return (
    <RelayPullProgressProvider>
      <LinkRespondersProvider>
        <AuthenticatedAppProvidersTree {...props} />
      </LinkRespondersProvider>
    </RelayPullProgressProvider>
  )
}

function AuthenticatedAppProvidersTree({ adapter, navigation, recordingRoute, dialogs, children }: AuthenticatedAppProvidersProps) {
  return (
    <AuthProvider adapter={adapter}>
      <EntitlementProvider>
        <PersistenceSetup>
          <OrgP2pTransportCoordinatorMount />
          <CanvasP2pAutoSyncMount />
          <BoardP2pAutoSyncMount />
          <BackgroundProjectPublishersMount />
          <SpacePrivacyRuleMount />
          <WikiP2pAutoSyncMount />
          <ProjectSettingsP2pSyncMount />
          <RegistryChangeSignalMount />
          <FeedbackContextPublisher />
          <MessagesProvider>
            <DataNotFoundMount />
            {dialogs}
            <RecordingProvider>
              <NavigationProvider value={navigation}>
                <RecordingContextBridge route={recordingRoute}>
                  <SoftReloginBanner />
                  <ContentDeleteRefusedBanner />
                  <CanvasWriteRefusedBanner />
                  <RelayPullProgressLine />
                  <ZvenixToolsProvider>{children}</ZvenixToolsProvider>
                </RecordingContextBridge>
              </NavigationProvider>
            </RecordingProvider>
          </MessagesProvider>
        </PersistenceSetup>
      </EntitlementProvider>
    </AuthProvider>
  )
}
